import { Component, signal } from '@angular/core';
import {
  CompilationsDataService,
  Compilation
} from 'quest-atlas-angular-components';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { ModalButton } from '../../common-components/modal/modal.component';
import { UserService } from '../../services/user.service';
import { TranslocoService } from '@ngneat/transloco';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TripShareService } from '../../services/trip-share.service';

@Component({
  selector: 'app-trips',
  templateUrl: './trips.component.html',
  styleUrl: './trips.component.scss',
  standalone: false
})
export class TripsComponent {
  trips = rxResource({
    stream: () => this.compilationsService.getTrips$('mine')
  });

  showDeleteModal = signal(false);
  showShareModal = signal(false);
  selectedTrip = signal<Compilation | null>(null);
  shareToken = signal<string | null>(null);

  user = toSignal(this.userService.getUserInfo$());

  deleteModalButtons: ModalButton[] = [
    {
      type: 'secondary',
      text: this.transloco.translate('Cancel'),
      onClick: () => this.showDeleteModal.set(false)
    },
    {
      type: 'error',
      text: this.transloco.translate('Delete'),
      onClick: () => this.confirmDelete()
    }
  ];

  shareModalButtons: ModalButton[] = [
    {
      type: 'primary',
      text: this.transloco.translate('close'),
      onClick: () => this.showShareModal.set(false)
    }
  ];

  constructor(
    private compilationsService: CompilationsDataService,
    private userService: UserService,
    private transloco: TranslocoService,
    private router: Router,
    private snackBar: MatSnackBar,
    private tripShareService: TripShareService
  ) {}

  createNewTrip(): void {
    this.router.navigate(['/edit-trip', 'new']);
  }

  editTrip(trip: Compilation): void {
    this.router.navigate(['/edit-trip', trip.id]);
  }

  viewTrip(trip: Compilation): void {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/trip', trip.id])
    );
    window.open(url, '_blank');
  }

  openShareModal(trip: Compilation): void {
    this.selectedTrip.set(trip);
    this.shareToken.set(null);

    if (trip.id) {
      const isMine = this.user()?.id === trip.creator.id;

      this.tripShareService.getShareLink(trip.id, isMine).subscribe({
        next: (shareUrl) => {
          // Extract token from the URL for backward compatibility
          if (isMine) {
            const token = shareUrl.split('/').pop();
            this.shareToken.set(token || null);
          } else {
            this.shareToken.set(`public-${trip.id}`);
          }
          this.showShareModal.set(true);
        },
        error: (error) => {
          console.error('Error creating share link:', error);
        }
      });
    }
  }

  openDeleteModal(trip: Compilation): void {
    this.selectedTrip.set(trip);
    this.showDeleteModal.set(true);
  }

  confirmDelete(): void {
    const trip = this.selectedTrip();
    if (trip?.id) {
      this.compilationsService.deleteCompilation$(trip.id).subscribe({
        next: () => {
          this.showDeleteModal.set(false);
          this.trips.reload();
        },
        error: (error) => {
          console.error('Error deleting trip:', error);
        }
      });
    }
  }

  copyShareLink(): void {
    const shareUrl = this.getShareUrl();
    if (shareUrl) {
      this.tripShareService.copyToClipboard(shareUrl).then(() => {
        this.showShareModal.set(false);
        this.snackBar.open(
          this.transloco.translate('linkCopiedToClipboard'),
          this.transloco.translate('close'),
          {
            duration: 3000,
            verticalPosition: 'bottom',
            horizontalPosition: 'center'
          }
        );
      });
    }
  }

  getShareUrl(): string {
    const token = this.shareToken();
    if (!token) return '';

    // Check if it's a public trip link (starts with 'public-')
    if (token.startsWith('public-')) {
      const tripId = token.replace('public-', '');
      return `${window.location.origin}/show-compilation/${tripId}`;
    }

    // For owned trips with share token
    return `${window.location.origin}/accept-share/compilation/${token}`;
  }

  getTripImage(trip: Compilation): string {
    // TODO: Get actual trip image from trip data
    // For now, return a placeholder based on the first location or a default image
    return trip.tripConfig?.titleImageUrl ?? 'assets/vectors/logo.svg';
  }


  viewOriginalTrip(trip: Compilation): void {
    if (trip.originalTrip) {
      const url = this.router.serializeUrl(
        this.router.createUrlTree(['/trip', trip.originalTrip])
      );
      window.open(url, '_blank');
    }
  }
}
